// frontend/src/components/ChantierList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner, Badge, ProgressBar } from 'react-bootstrap';
import api from '../api';
import { exportChantiers } from '../utils/exportExcel';
import GanttView from './GanttView';

const STATUTS = ['devis', 'en_cours', 'termine', 'annule'];

const STATUT_BADGE = {
  devis:     { bg: 'secondary', label: 'Devis' },
  en_cours:  { bg: 'primary',   label: 'En cours' },
  termine:   { bg: 'success',   label: 'Terminé' },
  annule:    { bg: 'danger',    label: 'Annulé' },
};

const EMPTY_FORM = {
  client_id: '',
  nom: '',
  description: '',
  statut: 'devis',
  adresse_chantier: '',
  date_debut: '',
  date_fin_prevue: '',
  budget: ''
};

function ChantierList({ onChantierChange }) {
  const [chantiers, setChantiers]         = useState([]);
  const [clients, setClients]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [showGantt, setShowGantt]         = useState(false);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const [editChantier, setEditChantier]   = useState(null);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [submitting, setSubmitting]       = useState(false);
  const [filtreStatut, setFiltreStatut]   = useState('');

  const fetchChantiers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filtreStatut ? `?statut=${filtreStatut}` : '';
      const response = await api.get(`/chantiers${params}`);
      const data = response.data;
      setChantiers(data);
      if (onChantierChange) onChantierChange(data.length);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filtreStatut]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await api.get('/clients?per_page=100');
      setClients(response.data.clients || []);
    } catch (err) {
      console.error('Erreur chargement clients:', err);
    }
  }, []);

  useEffect(() => { fetchChantiers(); }, [fetchChantiers]);
  useEffect(() => { fetchClients(); }, [fetchClients]);

  const openCreateModal = () => {
    setEditChantier(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (chantier) => {
    setEditChantier(chantier);
    setForm({
      client_id:        chantier.client_id,
      nom:              chantier.nom,
      description:      chantier.description || '',
      statut:           chantier.statut,
      adresse_chantier: chantier.adresse_chantier || '',
      date_debut:       chantier.date_debut ? chantier.date_debut.substring(0, 10) : '',
      date_fin_prevue:  chantier.date_fin_prevue ? chantier.date_fin_prevue.substring(0, 10) : '',
      budget:           chantier.budget || ''
    });
    setError(null);
    setShowModal(true);
  };

  const openGantt = (chantier) => {
    setSelectedChantier(chantier);
    setShowGantt(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload = {
      ...form,
      budget:    form.budget    ? parseFloat(form.budget)  : null,
      client_id: form.client_id ? parseInt(form.client_id) : null,
      date_debut:      form.date_debut      || null,
      date_fin_prevue: form.date_fin_prevue || null,
    };

    try {
      if (editChantier) {
        await api.put(`/chantiers/${editChantier.id}`, payload);
      } else {
        await api.post('/chantiers', payload);
      }
      setShowModal(false);
      await fetchChantiers();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Vue Gantt plein écran
  if (showGantt && selectedChantier) {
    return (
      <div style={{ padding: '16px' }}>
        <GanttView
          chantier={selectedChantier}
          onClose={() => { setShowGantt(false); fetchChantiers(); }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Chargement des chantiers...</p>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Liste des chantiers ({chantiers.length})</h4>
          <Form.Select
            size="sm"
            style={{ width: '150px' }}
            value={filtreStatut}
            onChange={(e) => setFiltreStatut(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            {STATUTS.map(s => (
              <option key={s} value={s}>{STATUT_BADGE[s].label}</option>
            ))}
          </Form.Select>
        </div>
        <Button variant="outline-success" className="me-2" onClick={() => exportChantiers(chantiers)}>📊 Excel</Button>
        <Button variant="primary" onClick={openCreateModal}>
          + Nouveau Chantier
        </Button>
      </div>

      {chantiers.length === 0 ? (
        <Alert variant="info">Aucun chantier pour le moment.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>#</th>
              <th>Chantier</th>
              <th>Client</th>
              <th>Statut</th>
              <th>Avancement</th>
              <th>Date début</th>
              <th>Date fin prévue</th>
              <th>Budget</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {chantiers.map((c) => (
              <tr key={c.id}>
                <td>{c.id}</td>
                <td>
                  <strong>{c.nom}</strong>
                  {c.adresse_chantier && (
                    <div><small className="text-muted">📍 {c.adresse_chantier}</small></div>
                  )}
                </td>
                <td>
                  {c.client_nom}
                  {c.client_entreprise && (
                    <div><small className="text-muted">{c.client_entreprise}</small></div>
                  )}
                </td>
                <td>
                  <Badge bg={STATUT_BADGE[c.statut]?.bg || 'secondary'}>
                    {STATUT_BADGE[c.statut]?.label || c.statut}
                  </Badge>
                </td>
                <td style={{ minWidth: '100px' }}>
                  <ProgressBar
                    now={c.avancement || 0}
                    label={`${c.avancement || 0}%`}
                    style={{ height: '16px' }}
                    variant={c.avancement === 100 ? 'success' : 'primary'}
                  />
                </td>
                <td>{c.date_debut ? c.date_debut.substring(0, 10) : '-'}</td>
                <td>{c.date_fin_prevue ? c.date_fin_prevue.substring(0, 10) : '-'}</td>
                <td>{c.budget ? `${Number(c.budget).toLocaleString('fr-FR')} €` : '-'}</td>
                <td>
                  <div className="d-flex gap-1">
                    <Button size="sm" variant="outline-info" onClick={() => openGantt(c)}
                      title="Planning Gantt">
                      📅 Gantt
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => openEditModal(c)}
                      title="Modifier">
                      ✏️
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Modal création / édition */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editChantier ? '✏️ Modifier le chantier' : '🏗️ Nouveau Chantier'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <div className="row">
              <div className="col-md-8">
                <Form.Group className="mb-3">
                  <Form.Label>Nom du chantier *</Form.Label>
                  <Form.Control type="text" name="nom" required value={form.nom} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Statut</Form.Label>
                  <Form.Select name="statut" value={form.statut} onChange={handleField}>
                    {STATUTS.map(s => (
                      <option key={s} value={s}>{STATUT_BADGE[s].label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Client *</Form.Label>
              <Form.Select name="client_id" required value={form.client_id} onChange={handleField}>
                <option value="">-- Sélectionner un client --</option>
                {clients.map(cl => (
                  <option key={cl.id} value={cl.id}>
                    {cl.prenom} {cl.nom} {cl.entreprise ? `(${cl.entreprise})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} name="description" value={form.description} onChange={handleField} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Adresse du chantier</Form.Label>
              <Form.Control type="text" name="adresse_chantier" value={form.adresse_chantier} onChange={handleField} />
            </Form.Group>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Date de début</Form.Label>
                  <Form.Control type="date" name="date_debut" value={form.date_debut} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Date de fin prévue</Form.Label>
                  <Form.Control type="date" name="date_fin_prevue" value={form.date_fin_prevue} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Budget (€)</Form.Label>
                  <Form.Control type="number" name="budget" min="0" step="0.01" value={form.budget} onChange={handleField} />
                </Form.Group>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting
                ? (editChantier ? 'Mise à jour...' : 'Création...')
                : (editChantier ? 'Mettre à jour' : 'Créer le chantier')
              }
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default ChantierList;
